import { useI18n } from "@/context/I18nContext";

/**
 * Chlorella cell biology as a plain textbook-style figure (after the
 * manufacturer's explainer at chlorella.co.jp/en/about/chlorella/): a
 * near-spherical single cell with its cup-shaped chloroplast, and the
 * four-divisions-in-twenty-hours cycle that turns one cell into sixteen.
 * Deliberately un-card-like — no dark panel or telemetry chrome — so it reads
 * as a scientific diagram rather than promotional material.
 */
export function AnimatedChlorellaBiology() {
  const { t } = useI18n();
  const daughters = [
    { cx: 396, cy: 64 },
    { cx: 452, cy: 96 },
    { cx: 396, cy: 128 },
    { cx: 452, cy: 160 },
  ];
  return (
    <figure className="w-full">
      <svg
        viewBox="0 0 520 250"
        className="w-full h-auto max-w-2xl mx-auto"
        role="img"
        aria-label={t("productDetail.sv12Japan.bio.cellLabel")}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Mother cell: double membrane, cup-shaped chloroplast, nucleus */}
        <circle cx="110" cy="112" r="62" fill="#dcfce7" stroke="#15803d" strokeWidth="2.5" />
        <circle
          cx="110"
          cy="112"
          r="55"
          fill="none"
          stroke="#15803d"
          strokeWidth="1"
          opacity="0.45"
        />
        <path
          d="M 72 132 A 48 48 0 1 1 148 132 A 34 34 0 1 0 72 132 Z"
          fill="#22c55e"
          opacity="0.75"
          className="animate-pulse-glow"
        />
        <circle cx="110" cy="118" r="13" fill="#f0fdf4" stroke="#15803d" strokeWidth="1.5" />
        <circle cx="110" cy="118" r="5" fill="#15803d" opacity="0.6" />

        {/* Chlorophyll leader line */}
        <line x1="146" y1="70" x2="196" y2="42" stroke="#15803d" strokeWidth="1" opacity="0.7" />
        <circle cx="146" cy="70" r="2" fill="#15803d" />
        <text x="200" y="40" fill="#166534" fontSize="13" fontWeight="600">
          {t("productDetail.sv12Japan.bio.chlorophyllLabel")}
        </text>

        {/* Scale bar */}
        <line x1="60" y1="196" x2="160" y2="196" stroke="#334155" strokeWidth="1.5" />
        <line x1="60" y1="191" x2="60" y2="201" stroke="#334155" strokeWidth="1.5" />
        <line x1="160" y1="191" x2="160" y2="201" stroke="#334155" strokeWidth="1.5" />
        <text x="110" y="216" textAnchor="middle" fill="#334155" fontSize="13" fontWeight="600">
          3–6 µm
        </text>

        {/* Sunlight */}
        <circle cx="270" cy="48" r="15" fill="none" stroke="#d97706" strokeWidth="2" />
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i * Math.PI) / 4;
          return (
            <line
              key={i}
              x1={270 + Math.cos(a) * 20}
              y1={48 + Math.sin(a) * 20}
              x2={270 + Math.cos(a) * 27}
              y2={48 + Math.sin(a) * 27}
              stroke="#d97706"
              strokeWidth="2"
              strokeLinecap="round"
            />
          );
        })}

        {/* Division arrow */}
        <path
          d="M 186 112 H 360"
          stroke="#334155"
          strokeWidth="1.5"
          strokeDasharray="6 5"
          opacity="0.8"
        />
        <path d="M 366 112 l -12 -7 v 14 Z" fill="#334155" opacity="0.9" />
        <text x="272" y="136" textAnchor="middle" fill="#334155" fontSize="13" fontWeight="600">
          {t("productDetail.sv12Japan.bio.divisionLabel")}
        </text>

        {/* Daughter cells appearing one by one */}
        {daughters.map((d, i) => (
          <g key={i} className="animate-cell-pop" style={{ animationDelay: `${i * 0.6}s` }}>
            <circle cx={d.cx} cy={d.cy} r="24" fill="#dcfce7" stroke="#15803d" strokeWidth="2" />
            <path
              d={`M ${d.cx - 15} ${d.cy + 8} A 18 18 0 1 1 ${d.cx + 15} ${d.cy + 8} A 13 13 0 1 0 ${d.cx - 15} ${d.cy + 8} Z`}
              fill="#22c55e"
              opacity="0.75"
            />
          </g>
        ))}
        <text x="424" y="216" textAnchor="middle" fill="#334155" fontSize="13" fontWeight="600">
          {t("productDetail.sv12Japan.bio.resultLabel")}
        </text>
      </svg>
      <figcaption className="mt-2 text-center text-xs text-muted-foreground">
        {t("productDetail.sv12Japan.bio.cellLabel")}
      </figcaption>
    </figure>
  );
}
